import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import { type ColDef, type ColGroupDef } from "ag-grid-community";
import capitalize from "lodash/capitalize";
import { CheckmarkIcon, Layout10Icon, MinusIcon, Pin01Icon } from "vega-icons";

import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { Badge } from "@vega-core/components/shadcn/badge";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@vega-core/components/shadcn/dropdown-menu";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { ColumnOrderRow, isColumnOrderDragData } from "./ColumnOrderRow";
import { getColumnOrderReorderResult } from "./getColumnOrderReorderResult";
import {
  type DataGridAnyColDef,
  type DataGridColDef,
  type DataGridColGroupDef,
} from "./types";
import { getColumnId, isColumnGroup } from "./utils";

export type ColumnToggleState = {
  visibility?: Record<string, boolean>;
  pinning?: Record<string, "left" | false>;
  order?: string[];
};

/**
 * Plug-in persistence interface for `useColumnToggle`. The hook keeps an
 * internal mirror of `value` for snappy clicks; consumers handle the actual
 * storage (BE, localStorage, in-memory).
 */
export type ColumnToggleStorage = {
  value?: ColumnToggleState;
  onChange?: (next: ColumnToggleState) => void;
};

function getToggleableLeaves<T>(columns: DataGridAnyColDef<T>[]) {
  return columns
    .flatMap((col) => (isColumnGroup(col) ? col.children : [col]))
    .filter((leaf) => leaf.toggleable);
}

const resolvePinned = (
  userPin: "left" | false | undefined,
  defaultPin: ColDef["pinned"],
): ColDef["pinned"] => {
  if (userPin === undefined) return defaultPin;

  return userPin === false ? null : userPin;
};

export type ColumnToggleOptions = {
  enablePinning?: boolean;
  enableBulkSelect?: boolean;
  showVisibleCount?: boolean;
  enableReordering?: boolean;
  onReorder?: (orderedColumnIds: string[]) => void;
  isHideLocked?: (id: string) => boolean;
  toggleIcon?: ReactNode;
  checkIcon?: ReactNode;
  pinIcon?: ReactNode;
  minusIcon?: ReactNode;
};

export function useColumnToggle<T>(
  rawColumns: DataGridAnyColDef<T>[],
  storage?: ColumnToggleStorage,
  options?: ColumnToggleOptions,
) {
  const enablePinning = options?.enablePinning ?? false;
  const enableBulkSelect = options?.enableBulkSelect ?? false;
  const showVisibleCount = options?.showVisibleCount ?? false;
  const enableReordering = options?.enableReordering ?? false;
  const onReorder = options?.onReorder;
  const isHideLocked = options?.isHideLocked;
  const toggleIcon = options?.toggleIcon ?? <Layout10Icon />;
  const checkIcon = options?.checkIcon ?? <CheckmarkIcon className="size-4" />;
  const pinIcon = options?.pinIcon ?? <Pin01Icon className="size-3.5" />;
  const minusIcon = options?.minusIcon ?? <MinusIcon className="size-4" />;

  const toggleableLeaves = getToggleableLeaves(rawColumns);
  const toggleableIds = toggleableLeaves.map(getColumnId).filter(Boolean);

  const defaultVisibility = Object.fromEntries(
    toggleableLeaves
      .map((leaf) => [getColumnId(leaf), leaf.hide !== true] as const)
      .filter(([id]) => Boolean(id)),
  );

  const [state, setState] = useState<ColumnToggleState>(() => ({
    visibility: { ...defaultVisibility, ...(storage?.value?.visibility ?? {}) },
    pinning: storage?.value?.pinning ?? {},
    order: storage?.value?.order,
  }));

  // Sync internal state when the backing store delivers a new value (e.g.
  // async BE load finishing after first render). When the value clears after
  // having been set (e.g. selecting the "Default" layout), reset to the
  // built-in defaults rather than leaving the previous value's state in place
  // — and report that reset through onChange, so a consumer tracking the
  // working state for a later save doesn't keep the pre-reset snapshot.
  const previousStorageValueRef = useRef(storage?.value);
  useEffect(() => {
    if (storage?.value) {
      setState({
        visibility: {
          ...defaultVisibility,
          ...(storage.value.visibility ?? {}),
        },
        pinning: storage.value.pinning ?? {},
        order: storage.value.order,
      });
    } else if (previousStorageValueRef.current) {
      const resetState = {
        visibility: { ...defaultVisibility },
        pinning: {},
        order: undefined,
      };
      setState(resetState);
      storage?.onChange?.(resetState);
    }

    previousStorageValueRef.current = storage?.value;
    // defaultVisibility is intentionally not in deps — it's recomputed from
    // toggleableIds, which doesn't change unless `rawColumns` does, in which
    // case the value sync also handles the new shape.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storage?.value]);

  // Prime the storage with the current state on mount so consumers that only
  // read on save (e.g. "save current layout") capture defaults — not the
  // empty `{}` they'd see if onChange had never fired.
  const hasPrimedRef = useRef(false);
  useEffect(() => {
    if (hasPrimedRef.current || !storage?.onChange) return;
    hasPrimedRef.current = true;
    storage.onChange(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storage]);

  const apply = (next: ColumnToggleState) => {
    setState(next);
    storage?.onChange?.(next);
  };

  const isVisible = (id: string) => state.visibility?.[id] ?? true;
  const pinSide = (id: string) => state.pinning?.[id];
  const isLockedForHiding = (id: string) =>
    isVisible(id) && Boolean(isHideLocked?.(id));

  // Pinning only makes sense for a visible column, so hiding a column always
  // drops its pin — otherwise the menu can show a hidden column as pinned.
  const withoutPins = (
    pinning: ColumnToggleState["pinning"],
    ids: string[],
  ): ColumnToggleState["pinning"] => {
    if (!pinning) return pinning;
    const drop = new Set(ids);

    return Object.fromEntries(
      Object.entries(pinning).filter(([id]) => !drop.has(id)),
    );
  };

  const setVisibility = (id: string, visible: boolean) => {
    apply({
      ...state,
      visibility: { ...state.visibility, [id]: visible },
      pinning: visible ? state.pinning : withoutPins(state.pinning, [id]),
    });
  };

  const toggle = (id: string) => setVisibility(id, !isVisible(id));

  const setGroupVisibility = (ids: string[], nextVisible: boolean) => {
    apply({
      ...state,
      visibility: {
        ...state.visibility,
        ...Object.fromEntries(ids.map((id) => [id, nextVisible])),
      },
      pinning: nextVisible ? state.pinning : withoutPins(state.pinning, ids),
    });
  };

  const togglePin = (id: string) => {
    const current = state.pinning?.[id];
    apply({
      ...state,
      pinning: {
        ...state.pinning,
        [id]: current === "left" ? false : "left",
      },
    });
  };

  const resolveOrderedIds = (order: string[] | undefined): string[] => {
    const known = new Set(toggleableIds);
    const fromOrder = (order ?? []).filter((id) => known.has(id));
    const seen = new Set(fromOrder);
    const missing = toggleableIds.filter((id) => !seen.has(id));

    return [...fromOrder, ...missing];
  };

  const orderedToggleableIds = enableReordering
    ? resolveOrderedIds(state.order)
    : toggleableIds;

  const substituteToggleableOrder = (
    order: string[],
  ): DataGridAnyColDef<T>[] => {
    const leafById = new Map(
      toggleableLeaves.map((leaf) => [getColumnId(leaf), leaf] as const),
    );
    let cursor = 0;

    return rawColumns.map((col) => {
      if (isColumnGroup(col) || !col.toggleable) return col;

      const id = order[cursor];
      cursor += 1;

      return (id && leafById.get(id)) || col;
    });
  };

  const getFullLeafOrder = (cols: DataGridAnyColDef<T>[]): string[] =>
    cols
      .flatMap((col) => (isColumnGroup(col) ? col.children : [col]))
      .map(getColumnId)
      .filter(Boolean);

  const orderedRawColumns = enableReordering
    ? substituteToggleableOrder(orderedToggleableIds)
    : rawColumns;

  // Apply user-controlled visibility + pinning to each colDef. User pin
  // overrides the static `col.pinned` only when explicitly set (left or
  // false); when undefined we preserve the developer-set default.
  const applyUserState = ({
    toggleable,
    ...col
  }: DataGridColDef<T>): ColDef<T> => {
    const id = getColumnId(col);
    const hidden = toggleable && !isVisible(id);

    return {
      ...col,
      hide: hidden,
      pinned: hidden
        ? col.pinned
        : resolvePinned(state.pinning?.[id], col.pinned),
    };
  };

  const columnDefs: (ColDef<T> | ColGroupDef<T>)[] = orderedRawColumns.map(
    (col) =>
      isColumnGroup(col)
        ? { ...col, children: col.children.map(applyUserState) }
        : applyUserState(col),
  );

  const latestReorderRef = useRef({
    state,
    apply,
    onReorder,
    orderedToggleableIds,
    substituteToggleableOrder,
    getFullLeafOrder,
  });
  useEffect(() => {
    latestReorderRef.current = {
      state,
      apply,
      onReorder,
      orderedToggleableIds,
      substituteToggleableOrder,
      getFullLeafOrder,
    };
  });

  useEffect(() => {
    if (!enableReordering) return undefined;

    return monitorForElements({
      canMonitor: ({ source }) => isColumnOrderDragData(source.data),
      onDrop: ({ source, location }) => {
        if (location.current.dropTargets.length === 0) return;

        const sourceData = source.data;
        const targetData = location.current.dropTargets[0].data;

        if (
          !isColumnOrderDragData(sourceData) ||
          !isColumnOrderDragData(targetData)
        ) {
          return;
        }

        const current = latestReorderRef.current;
        const newOrder = getColumnOrderReorderResult(
          sourceData.columnId,
          targetData.columnId,
          current.orderedToggleableIds,
        );

        if (!newOrder) return;

        current.apply({ ...current.state, order: newOrder });
        current.onReorder?.(
          current.getFullLeafOrder(current.substituteToggleableOrder(newOrder)),
        );
      },
    });
  }, [enableReordering]);

  const renderPinButton = (id: string) => {
    const pinned = pinSide(id) === "left";

    return (
      <button
        aria-label={pinned ? m.dataGrid_unpin() : m.dataGrid_pinLeft()}
        className={cnTwMerge(
          "ml-2 p-1 rounded hover:bg-background/secondary",
          pinned ? "text-brand-default" : "text-text/tertiary",
        )}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          togglePin(id);
        }}
        type="button"
      >
        {pinIcon}
      </button>
    );
  };

  const supportsPinning = enablePinning;

  const renderLeafCheckbox = ({
    leaf,
    indented = false,
  }: {
    leaf: DataGridColDef<T>;
    indented?: boolean;
  }) => {
    const id = getColumnId(leaf);
    const label = leaf.headerName ?? capitalize(leaf.field ?? "");
    const locked = isLockedForHiding(id);

    return (
      <Tooltip
        content={locked ? m.dataGrid_clearFilterToHide() : undefined}
        key={id}
      >
        <span className="block" data-testid={`column-checkbox-row-${id}`}>
          <DropdownMenuCheckboxItem
            checked={isVisible(id)}
            checkedIcon={checkIcon}
            className={indented ? "ml-6" : undefined}
            disabled={locked}
            onCheckedChange={() => toggle(id)}
            onSelect={(event) => event.preventDefault()}
          >
            <span className="flex-1 truncate">{label}</span>
            {locked ? (
              <Badge
                className="ml-2 shrink-0 rounded-small border-transparent bg-background/component/10 px-1.5 py-0.5 body-extra-small-medium text-text/secondary"
                variant="outline"
              >
                {m.dataGrid_filteredColumnTag()}
              </Badge>
            ) : null}
            {supportsPinning && isVisible(id) ? renderPinButton(id) : null}
          </DropdownMenuCheckboxItem>
        </span>
      </Tooltip>
    );
  };

  const renderReorderableLeaf = (id: string) => {
    const leaf = toggleableLeaves.find(
      (candidate) => getColumnId(candidate) === id,
    );

    if (!leaf) return null;

    const label = leaf.headerName ?? capitalize(leaf.field ?? "");
    const locked = isLockedForHiding(id);

    return (
      <ColumnOrderRow
        checked={isVisible(id)}
        checkedIcon={checkIcon}
        disabled={locked}
        disabledTooltip={m.dataGrid_clearFilterToHide()}
        id={id}
        key={id}
        label={label}
        onCheckedChange={() => toggle(id)}
        trailing={supportsPinning && isVisible(id) ? renderPinButton(id) : null}
      />
    );
  };

  const renderGroup = (group: DataGridColGroupDef<T>) => {
    const toggleableChildren = group.children.filter((col) => col.toggleable);

    if (toggleableChildren.length === 0) return null;

    // Blank-header groups have no meaningful label — render their children
    // as flat checkboxes, matching the ungrouped layout.
    if (!group.headerName) {
      return (
        <Fragment key={group.groupId ?? "ungrouped"}>
          {toggleableChildren.map((leaf) => renderLeafCheckbox({ leaf }))}
        </Fragment>
      );
    }

    const childIds = toggleableChildren.map(getColumnId);
    const visibleCount = childIds.filter(isVisible).length;
    const allVisible = visibleCount === childIds.length;
    const someVisible = visibleCount > 0 && !allVisible;

    return (
      <div key={group.groupId ?? group.headerName}>
        <DropdownMenuItem
          className="pl-2 font-semibold"
          onSelect={(event) => {
            event.preventDefault();
            setGroupVisibility(childIds, !allVisible);
          }}
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            {allVisible ? checkIcon : null}
            {someVisible ? minusIcon : null}
          </span>
          {group.headerName}
        </DropdownMenuItem>
        {toggleableChildren.map((leaf) =>
          renderLeafCheckbox({ leaf, indented: true }),
        )}
      </div>
    );
  };

  const renderColumn = (col: DataGridAnyColDef<T>) => {
    if (isColumnGroup(col)) return renderGroup(col);

    return col.toggleable ? renderLeafCheckbox({ leaf: col }) : null;
  };

  const allVisible = toggleableIds.every(isVisible);
  const visibleCount = toggleableIds.filter(isVisible).length;
  const idsForLockCheck = enableReordering
    ? orderedToggleableIds
    : toggleableIds;
  const hasLockedColumns = idsForLockCheck.some(isLockedForHiding);

  const renderColumnToggle = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ThemedButton
          dataTestId="column-toggle-button"
          icon={toggleIcon}
          size="small"
          variant="secondary"
        >
          {m.dataGrid_columns()}
          {showVisibleCount ? ` ${visibleCount}` : null}
        </ThemedButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="p-2 max-h-80 overflow-y-auto">
        <DropdownMenuLabel>{m.dataGrid_toggleColumns()}</DropdownMenuLabel>
        {hasLockedColumns ? (
          <p className="px-2 pb-1.5 body-small-regular text-text/tertiary">
            {m.dataGrid_clearFilterBeforeHiding()}
          </p>
        ) : null}
        <DropdownMenuSeparator />
        {enableBulkSelect && toggleableIds.length > 0 ? (
          <>
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                setGroupVisibility(toggleableIds, !allVisible);
              }}
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                {allVisible ? checkIcon : null}
              </span>
              {allVisible ? m.dataGrid_deselectAll() : m.dataGrid_selectAll()}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        {enableReordering
          ? orderedToggleableIds.map(renderReorderableLeaf)
          : rawColumns.map(renderColumn)}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return { columnDefs, renderColumnToggle };
}
